import React from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
    Boxes,
    Building2,
    ChevronDown,
    ClipboardList,
    Cog,
    CircleDollarSign,
    FileText,
    HardHat,
    LayoutDashboard,
    LogOut,
    Settings,
    SlidersHorizontal,
    Truck,
    User,
    UserCog,
    Users,
    Wallet,
} from "lucide-react";
import { cn, initialsOf } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useAuth } from "@/contexts/AuthContext";
import { usePermission } from "@/hooks/usePermission";
import { ROLE_LABELS } from "@/config/constants";
import { BrandInline } from "@/components/common/BrandLogo";
import { proposalsService } from "@/services/proposals.service";
import { financeService } from "@/services/finance.service";

/**
 * Sidebar organization (synthesized to 3 top-level groups):
 *   1. Operações       — daily workflow: OS, oficina, propostas, clientes, ativos
 *   2. Financeiro      — Admin-only, collapsible: dashboard, AR, AP, fornecedores
 *   3. Configurações   — Admin/User: empresa, usuários, perfil (collapsible)
 *
 * The dashboard is pinned above the groups since it is the entry point for
 * every user regardless of role.
 */
const NAV_GROUPS = [
    {
        label: "Operações",
        items: [
            { to: "/customers", label: "Clientes", icon: Users, resource: "customers", testid: "sidebar-nav-customers" },
            { to: "/assets", label: "Equipamentos", icon: Boxes, resource: "assets", testid: "sidebar-nav-assets" },
            { to: "/work-orders", label: "Ordens de Serviço", icon: ClipboardList, resource: "work_orders", testid: "sidebar-nav-work-orders" },
            { to: "/workshop", label: "Oficina", icon: Cog, resource: "workshop", testid: "sidebar-nav-workshop" },
            {
                to: "/proposals", label: "Propostas", icon: FileText,
                resource: "commercial_proposals", testid: "sidebar-nav-proposals",
                badgeSource: "proposalsPending",
            },
        ],
    },
    {
        label: "Financeiro",
        collapsible: true,
        icon: Wallet,
        adminOnly: true,
        pathPrefix: "/finance",
        items: [
            { to: "/finance", label: "Dashboard", icon: LayoutDashboard, resource: "finance", testid: "sidebar-nav-finance-dashboard" },
            {
                to: "/finance/receivables", label: "A Receber", icon: CircleDollarSign,
                resource: "finance", testid: "sidebar-nav-receivables",
                badgeSource: "overdueReceivable",
            },
            {
                to: "/finance/payables", label: "A Pagar", icon: CircleDollarSign,
                resource: "finance", testid: "sidebar-nav-payables",
                badgeSource: "overduePayable",
            },
            { to: "/finance/suppliers", label: "Fornecedores", icon: Truck, resource: "finance", testid: "sidebar-nav-suppliers" },
        ],
    },
    {
        label: "Configurações",
        collapsible: true,
        icon: Settings,
        pathPrefix: "/settings",
        items: [
            { to: "/settings/company", label: "Empresa", icon: Building2, resource: "company", testid: "sidebar-nav-company", adminOnly: true },
            { to: "/users", label: "Usuários", icon: UserCog, resource: "users", testid: "sidebar-nav-users", adminOnly: true },
            { to: "/technicians", label: "Técnicos", icon: HardHat, resource: "technicians", testid: "sidebar-nav-technicians", adminOnly: true },
            { to: "/profile", label: "Perfil", icon: User, resource: null, testid: "sidebar-nav-profile" },
            { to: "/settings", label: "Preferências", icon: SlidersHorizontal, resource: null, testid: "sidebar-nav-preferences", adminOnly: true },
        ],
    },
];

function useCollapsibleAutoOpen(pathname, groups) {
    const matches = React.useCallback(
        (group) => {
            if (pathname.startsWith(group.pathPrefix)) return true;
            return group.items.some((item) => item.to && pathname.startsWith(item.to));
        },
        [pathname],
    );
    const initial = React.useMemo(
        () => Object.fromEntries(groups.map((g) => [g.pathPrefix, matches(g)])),
        [], // eslint-disable-line react-hooks/exhaustive-deps
    );
    const [openMap, setOpenMap] = React.useState(initial);
    React.useEffect(() => {
        setOpenMap((prev) => {
            const next = { ...prev };
            for (const g of groups) {
                if (matches(g)) next[g.pathPrefix] = true;
            }
            return next;
        });
    }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
    const setOpen = React.useCallback((prefix, value) => {
        setOpenMap((prev) => ({ ...prev, [prefix]: value }));
    }, []);
    return [openMap, setOpen];
}

function BadgePill({ value, tone = "warning", testid }) {
    if (!value || value <= 0) return null;
    const toneClass = tone === "danger" ? "bg-red-500" : "bg-yellow-500";
    return (
        <span
            className={cn(
                "ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold text-white",
                toneClass,
            )}
            data-testid={testid}
        >
            {value}
        </span>
    );
}

function NavItem({ item, badgeValue, onNavigate }) {
    const tone =
        item.badgeSource === "overdueReceivable" || item.badgeSource === "overduePayable"
            ? "danger"
            : "warning";
    return (
        <li>
            <NavLink
                to={item.to}
                end
                onClick={onNavigate}
                className={({ isActive }) =>
                    cn(
                        "group relative flex h-9 items-center gap-3 rounded-lg px-3 text-sm font-medium",
                        isActive
                            ? "bg-accent text-foreground"
                            : "text-muted-foreground hover:bg-accent/60 hover:text-foreground",
                    )
                }
                data-testid={item.testid}
            >
                {({ isActive }) => (
                    <>
                        {isActive && (
                            <span className="absolute inset-y-1 left-0 w-0.5 rounded-full bg-gold" />
                        )}
                        <item.icon className={cn("h-4 w-4 shrink-0", isActive && "text-gold")} />
                        <span className="flex-1 truncate">{item.label}</span>
                        <BadgePill
                            value={badgeValue}
                            tone={tone}
                            testid={`${item.testid}-badge`}
                        />
                    </>
                )}
            </NavLink>
        </li>
    );
}

export function Sidebar({ onNavigate }) {
    const { user, logout } = useAuth();
    const { can } = usePermission();
    const navigate = useNavigate();
    const location = useLocation();
    const isAdmin = user?.role === "Administrator";

    // Badge signals
    const proposalsDashboardQ = useQuery({
        queryKey: ["proposals-dashboard"],
        queryFn: () => proposalsService.dashboard(),
        enabled: can("commercial_proposals", "read"),
        refetchInterval: 30000,
        refetchOnWindowFocus: true,
    });
    const financeDashboardQ = useQuery({
        queryKey: ["finance-dashboard"],
        queryFn: () => financeService.dashboard(),
        enabled: can("finance", "read"),
        refetchInterval: 60000,
        refetchOnWindowFocus: true,
    });
    const badges = {
        proposalsPending: proposalsDashboardQ.data?.workshops_pending_proposal || 0,
        overdueReceivable: financeDashboardQ.data?.kpis?.overdue_receivable_count || 0,
        overduePayable: financeDashboardQ.data?.kpis?.overdue_payable_count || 0,
    };

    // Collapsible state for each collapsible group
    const collapsibleGroups = React.useMemo(
        () => NAV_GROUPS.filter((g) => g.collapsible),
        [],
    );
    const [openMap, setOpen] = useCollapsibleAutoOpen(location.pathname, collapsibleGroups);

    const handleLogout = () => {
        logout();
        navigate("/login", { replace: true });
    };

    function filterItems(group) {
        return group.items.filter((item) => {
            if (item.adminOnly && !isAdmin) return false;
            return !item.resource || can(item.resource, "read");
        });
    }

    return (
        <div className="flex h-full flex-col">
            <div className="flex h-16 items-center gap-3 px-4">
                <BrandInline />
            </div>
            <Separator />

            <nav className="flex-1 overflow-y-auto px-3 py-3 p-360-thin-scrollbar" aria-label="Menu principal">
                {/* Dashboard is pinned above every group */}
                <ul className="mb-4 space-y-1">
                    <NavItem
                        item={{
                            to: "/dashboard",
                            label: "Dashboard",
                            icon: LayoutDashboard,
                            resource: "dashboard",
                            testid: "sidebar-nav-dashboard",
                        }}
                        onNavigate={onNavigate}
                    />
                </ul>

                {NAV_GROUPS.map((group) => {
                    if (group.adminOnly && !isAdmin) return null;
                    const items = filterItems(group);
                    if (items.length === 0) return null;

                    if (group.collapsible) {
                        const groupBadge =
                            group.pathPrefix === "/finance"
                                ? (badges.overdueReceivable || 0) + (badges.overduePayable || 0)
                                : 0;
                        const isOpen = !!openMap[group.pathPrefix];
                        return (
                            <Collapsible
                                key={group.label}
                                open={isOpen}
                                onOpenChange={(v) => setOpen(group.pathPrefix, v)}
                                className="mb-4"
                            >
                                <CollapsibleTrigger
                                    className="group flex w-full items-center gap-2 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
                                    data-testid={`sidebar-group-toggle-${group.pathPrefix.replace(/\//g, "")}`}
                                >
                                    <group.icon className="h-3.5 w-3.5" />
                                    <span className="flex-1 text-left">{group.label}</span>
                                    {groupBadge > 0 && !isOpen && (
                                        <span className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-semibold text-white">
                                            {groupBadge}
                                        </span>
                                    )}
                                    <ChevronDown
                                        className={cn(
                                            "h-3.5 w-3.5 transition-transform duration-200",
                                            isOpen && "rotate-180",
                                        )}
                                    />
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <ul className="space-y-1 pt-1">
                                        {items.map((item) => (
                                            <NavItem
                                                key={item.to}
                                                item={item}
                                                badgeValue={item.badgeSource ? badges[item.badgeSource] : 0}
                                                onNavigate={onNavigate}
                                            />
                                        ))}
                                    </ul>
                                </CollapsibleContent>
                            </Collapsible>
                        );
                    }

                    return (
                        <div key={group.label} className="mb-4">
                            <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                {group.label}
                            </div>
                            <ul className="space-y-1">
                                {items.map((item) => (
                                    <NavItem
                                        key={item.to}
                                        item={item}
                                        badgeValue={item.badgeSource ? badges[item.badgeSource] : 0}
                                        onNavigate={onNavigate}
                                    />
                                ))}
                            </ul>
                        </div>
                    );
                })}
            </nav>

            <Separator />
            <div className="flex items-center gap-3 p-3">
                <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-brand-navy text-primary-foreground text-xs">
                        {initialsOf(user?.name)}
                    </AvatarFallback>
                </Avatar>
                <div className="flex-1 overflow-hidden">
                    <div className="truncate text-sm font-medium" title={user?.name}>
                        {user?.name || "—"}
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Badge variant="secondary" className="px-1.5 py-0 text-[10px] font-medium" data-testid="sidebar-role-badge">
                            {ROLE_LABELS[user?.role] || user?.role || "—"}
                        </Badge>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={handleLogout}
                    className="rounded-lg p-2 text-muted-foreground hover:bg-accent/60 hover:text-foreground"
                    aria-label="Sair"
                    data-testid="sidebar-nav-logout"
                    title="Sair"
                >
                    <LogOut className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}
